import React, { useState, useEffect } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { useToast } from '../hooks/use-toast';
import { mockData } from '../mock';
import { Calendar as CalendarIcon, Clock, User, Mail, Phone, GraduationCap, Users, Upload, X } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Calendar } from '../components/ui/calendar';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const SignUp = () => {
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const { toast } = useToast();
  const [selectedGuide, setSelectedGuide] = useState(null);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState(null);
  const [step, setStep] = useState(1); // 1: Select Guide, 2: Select Time, 3: Fill Details
  
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    numberOfPeople: '1',
    specialRequirements: '',
    school: ''
  });

  // Guide Application State
  const [guideFormData, setGuideFormData] = useState({
    name: '',
    email: '',
    phone: '',
    school: '',
    bio: '',
    tourDescription: '',
    // New fields for comprehensive profile
    guide_type: '', // Local Tour Guide, Tour Operator, Campus Tour Guide
    gender: '',
    academic_year: '',
    age: '',
    admission_type: '',
    personality_type: '',
    college_rating: '',
    hometown: '',
    major: '',
    minor: '',
    clubs: '',
    college_experience: '',
    tip_for_students: '',
    favorite_class: '',
    career_goals: '',
    high_school: '',
    meeting_point: ''
  });
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [selectedDates, setSelectedDates] = useState([]);
  const [dateTimeMap, setDateTimeMap] = useState({}); // {dateString: [times]}
  const [currentEditingDate, setCurrentEditingDate] = useState(null);
  const [isGuideSubmitting, setIsGuideSubmitting] = useState(false);
  const [guideFormStep, setGuideFormStep] = useState(1); // Multi-step form: 1, 2, 3, 4
  const [guideExtracurriculars, setGuideExtracurriculars] = useState([]);
  const [guideHousingExperience, setGuideHousingExperience] = useState([]);

  const API = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

  // Time slots for guide availability
  const timeSlots = [
    '12:00 AM', '1:00 AM', '2:00 AM', '3:00 AM', '4:00 AM', '5:00 AM',
    '6:00 AM', '7:00 AM', '8:00 AM', '9:00 AM', '10:00 AM', '11:00 AM',
    '12:00 PM', '1:00 PM', '2:00 PM', '3:00 PM', '4:00 PM', '5:00 PM',
    '6:00 PM', '7:00 PM', '8:00 PM', '9:00 PM', '10:00 PM', '11:00 PM'
  ];

  const toggleTimeSlot = (time) => {
    if (!currentEditingDate) return;
    
    const dateKey = currentEditingDate.toDateString();
    setDateTimeMap(prev => {
      const currentTimes = prev[dateKey] || [];
      const newTimes = currentTimes.includes(time)
        ? currentTimes.filter(t => t !== time)
        : [...currentTimes, time];
      return { ...prev, [dateKey]: newTimes };
    });
  };

  const handleDateSelect = (dates) => {
    setSelectedDates(dates || []);
    // When a new date is selected, set it as the current editing date
    if (dates && dates.length > 0) {
      const latestDate = dates[dates.length - 1];
      setCurrentEditingDate(latestDate);
    } else {
      setCurrentEditingDate(null);
    }
  };

  const removeDate = (index) => {
    const dateToRemove = selectedDates[index];
    const dateKey = dateToRemove.toDateString();
    
    // Remove from selectedDates
    setSelectedDates(prev => prev.filter((_, i) => i !== index));
    
    // Remove times for this date
    setDateTimeMap(prev => {
      const newMap = { ...prev };
      delete newMap[dateKey];
      return newMap;
    });
    
    // If removing the currently editing date, switch to another
    if (currentEditingDate && currentEditingDate.toDateString() === dateKey) {
      const remainingDates = selectedDates.filter((_, i) => i !== index);
      setCurrentEditingDate(remainingDates.length > 0 ? remainingDates[0] : null);
    }
  };

  const handleGuideFormChange = (e) => {
    setGuideFormData({ ...guideFormData, [e.target.name]: e.target.value });
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    
    if (selectedFiles.length + files.length > 5) {
      toast({
        title: "Too many photos",
        description: "Maximum 5 photos allowed",
        variant: "destructive"
      });
      return;
    }
    
    const validFiles = [];
    for (const file of files) {
      if (file.size > 5 * 1024 * 1024) {
        toast({
          title: "File too large",
          description: `${file.name} is over 5MB and was skipped`,
          variant: "destructive"
        });
        continue;
      }
      validFiles.push(file);
    }
    
    setSelectedFiles(prev => [...prev, ...validFiles]);
  };

  const removePhoto = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleGuideSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedFiles || selectedFiles.length < 2) {
      toast({
        title: "Photos Required",
        description: "Please upload at least 2 photos before submitting.",
        variant: "destructive"
      });
      alert("Please upload at least 2 photos before submitting.");
      return;
    }
    
    if (!selectedDates || selectedDates.length === 0) {
      toast({
        title: "Dates Required",
        description: "Please select at least one available date.",
        variant: "destructive"
      });
      alert("Please select at least one available date on the calendar.");
      return;
    }
    
    // Check if all dates have at least one time selected
    const datesWithoutTimes = selectedDates.filter(date => {
      const dateKey = date.toDateString();
      const times = dateTimeMap[dateKey] || [];
      return times.length === 0;
    });
    
    if (datesWithoutTimes.length > 0) {
      toast({
        title: "Times Required", 
        description: "Please select times for all selected dates.",
        variant: "destructive"
      });
      alert("Please select available times for each date you've chosen.");
      return;
    }
    
    setIsGuideSubmitting(true);

    try {
      const submitData = new FormData();
      submitData.append('name', guideFormData.name);
      submitData.append('email', guideFormData.email);
      submitData.append('phone', guideFormData.phone);
      submitData.append('school', guideFormData.school);
      submitData.append('bio', guideFormData.bio);
      
      // Add guide type
      if (guideFormData.guide_type) submitData.append('guide_type', guideFormData.guide_type);
      
      // Add all the new comprehensive profile fields
      if (guideFormData.gender) submitData.append('gender', guideFormData.gender);
      if (guideFormData.academic_year) submitData.append('academic_year', guideFormData.academic_year);
      if (guideFormData.age) submitData.append('age', guideFormData.age);
      if (guideFormData.admission_type) submitData.append('admission_type', guideFormData.admission_type);
      if (guideFormData.personality_type) submitData.append('personality_type', guideFormData.personality_type);
      if (guideFormData.college_rating) submitData.append('college_rating', guideFormData.college_rating);
      if (guideFormData.hometown) submitData.append('hometown', guideFormData.hometown);
      if (guideFormData.major) submitData.append('major', guideFormData.major);
      if (guideFormData.minor) submitData.append('minor', guideFormData.minor);
      if (guideFormData.clubs) submitData.append('clubs', guideFormData.clubs);
      if (guideFormData.college_experience) submitData.append('college_experience', guideFormData.college_experience);
      if (guideFormData.tip_for_students) submitData.append('tip_for_students', guideFormData.tip_for_students);
      if (guideFormData.favorite_class) submitData.append('favorite_class', guideFormData.favorite_class);
      if (guideFormData.career_goals) submitData.append('career_goals', guideFormData.career_goals);
      if (guideFormData.high_school) submitData.append('high_school', guideFormData.high_school);
      if (guideFormData.meeting_point) submitData.append('meeting_point', guideFormData.meeting_point);
      
      // Add extracurriculars and housing experience
      if (guideExtracurriculars.length > 0) {
        submitData.append('extracurriculars', JSON.stringify(guideExtracurriculars));
      }
      if (guideHousingExperience.length > 0) {
        submitData.append('housing_experience', JSON.stringify(guideHousingExperience));
      }
      
      // Format availability with dates and their specific times
      const availabilityParts = selectedDates
        .sort((a, b) => a - b)
        .map(date => {
          const dateKey = date.toDateString();
          const dateStr = date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
          const times = dateTimeMap[dateKey] || [];
          return `${dateStr}: ${times.join(', ')}`;
        });
      const availabilityStr = availabilityParts.join(' | ');
      submitData.append('availability', availabilityStr);
      submitData.append('tour_description', guideFormData.tourDescription);
      
      // Add tour types
      const tourTypes = [];
      if (guideFormData.tourTypeCampus) tourTypes.push('Campus Tour');
      if (guideFormData.tourTypeVideo) tourTypes.push('Video Tour');
      submitData.append('tour_types', tourTypes.join(', '));
      
      for (let i = 0; i < selectedFiles.length; i++) {
        submitData.append('photos', selectedFiles[i]);
      }

      const response = await fetch(`${API}/api/guides/apply`, {
        method: 'POST',
        body: submitData
      });

      const responseData = await response.json();
      
      if (!response.ok) {
        throw new Error(responseData.detail || 'Application submission failed');
      }

      alert("Success! Your application has been submitted. We'll review it and get back to you soon!");
      
      toast({
        title: "Application Submitted! 🎉",
        description: "Thank you for applying! We'll review your application and get back to you soon.",
      });

      // Reset form
      setGuideFormData({
        name: '',
        email: '',
        phone: '',
        school: '',
        bio: '',
        tourDescription: '',
        guide_type: '',
        gender: '',
        academic_year: '',
        age: '',
        admission_type: '',
        personality_type: '',
        college_rating: '',
        hometown: '',
        major: '',
        minor: '',
        clubs: '',
        college_experience: '',
        tip_for_students: '',
        favorite_class: '',
        career_goals: '',
        high_school: '',
        meeting_point: ''
      });
      setSelectedFiles([]);
      setSelectedDates([]);
      setDateTimeMap({});
      setCurrentEditingDate(null);
      setGuideFormStep(1);
      setGuideExtracurriculars([]);
      setGuideHousingExperience([]);

    } catch (error) {
      console.error('Application error:', error);
      toast({
        title: "Submission Failed",
        description: error.message || 'Failed to submit application. Please try again.',
        variant: "destructive",
        duration: 10000
      });
      alert('Error: ' + (error.message || 'Failed to submit application. Please check your photos and try again.'));
    } finally {
      setIsGuideSubmitting(false);
    }
  };

  // Function to filter out past dates - only show future dates
  const filterFutureDates = (slots) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to start of day (midnight)
    
    return slots.filter(slot => {
      try {
        // Parse the date from the slot (format: "Month Day, Year" e.g., "October 16, 2025")
        const slotDate = new Date(slot.date);
        slotDate.setHours(0, 0, 0, 0);
        
        // Only show slots that are today or in the future
        return slotDate >= today;
      } catch (error) {
        console.error('Error parsing date:', slot.date, error);
        return false;
      }
    });
  };

  const handleGuideSelect = (guide) => {
    setSelectedGuide(guide);
    setStep(2);
  };

  const handleTimeSlotSelect = (timeSlot) => {
    setSelectedTimeSlot(timeSlot);
    setStep(3);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Prepare booking data for API
      const bookingData = {
        student_name: `${formData.firstName} ${formData.lastName}`,
        student_email: formData.email,
        phone: formData.phone,
        school: formData.school,
        instructor_id: selectedGuide.id,
        instructor_name: selectedGuide.name,
        time_slot_date: selectedTimeSlot.date,
        time_slot_time: selectedTimeSlot.time,
        number_of_people: formData.numberOfPeople,
        special_requirements: formData.specialRequirements || ''
      };
      
      // Send to backend API
      const API = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      const response = await fetch(`${API}/api/bookings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(bookingData)
      });
      
      if (!response.ok) {
        const error = await response.json();
        const errorMessage = typeof error.detail === 'string' 
          ? error.detail 
          : JSON.stringify(error.detail || error);
        throw new Error(errorMessage);
      }
      
      const result = await response.json();
      
      toast({
        title: "Booking Successful! 🎉",
        description: `Your class with ${selectedGuide.name} on ${selectedTimeSlot.date} at ${selectedTimeSlot.time} has been booked. We'll send you a confirmation email with class details shortly. Booking ID: ${result.id.substring(0, 8)}`,
      });
      
      // Reset form
      setSelectedGuide(null);
      setSelectedTimeSlot(null);
      setStep(1);
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        numberOfPeople: '1',
        specialRequirements: '',
        school: ''
      });
      
    } catch (error) {
      console.error('Booking error:', error);
      toast({
        title: "Booking Failed",
        description: error.message || 'Failed to create booking. Please try again.',
        variant: "destructive"
      });
    }
  };

  const goBack = () => {
    if (step === 3) {
      setSelectedTimeSlot(null);
      setStep(2);
    } else if (step === 2) {
      setSelectedGuide(null);
      setStep(1);
    }
  };

  return (
    <div className="min-h-screen">
      <Navbar />
      
      <div className="pt-24 pb-16 bg-gray-50 min-h-screen">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-5xl font-serif mb-4">Become a Tour Guide</h1>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Feel free to reach out to our tour guide instructor via email to learn how to become a tour guide.
            </p>
          </div>

          {/* Progress Indicator */}
          <div className="max-w-3xl mx-auto mb-12">
            <div className="flex items-center justify-center">
              <div className={`flex items-center ${step >= 1 ? 'text-black' : 'text-gray-400'}`}>
                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${
                  step >= 1 ? 'bg-black text-white' : 'bg-gray-300'
                }`}>1</div>
                <span className="ml-2 hidden sm:inline">Select Guide</span>
              </div>
              <div className={`h-1 w-16 mx-4 ${
                step >= 2 ? 'bg-black' : 'bg-gray-300'
              }`}></div>
              <div className={`flex items-center ${step >= 2 ? 'text-black' : 'text-gray-400'}`}>
                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${
                  step >= 2 ? 'bg-black text-white' : 'bg-gray-300'
                }`}>2</div>
                <span className="ml-2 hidden sm:inline">Select Time</span>
              </div>
              <div className={`h-1 w-16 mx-4 ${
                step >= 3 ? 'bg-black' : 'bg-gray-300'
              }`}></div>
              <div className={`flex items-center ${step >= 3 ? 'text-black' : 'text-gray-400'}`}>
                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${
                  step >= 3 ? 'bg-black text-white' : 'bg-gray-300'
                }`}>3</div>
                <span className="ml-2 hidden sm:inline">Your Details</span>
              </div>
            </div>
          </div>

          {/* Step 1: Select Guide */}
          {step === 1 && (
            <div>
              <h2 className="text-3xl font-serif text-center mb-8">Choose Your Instructor</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
                {mockData.instructors.map((guide) => (
                  <Card 
                    key={guide.id} 
                    className="cursor-pointer hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
                    onClick={() => handleGuideSelect(guide)}
                  >
                    <img 
                      src={guide.image} 
                      alt={guide.name}
                      className="w-full h-48 object-cover rounded-t-lg"
                    />
                    <CardHeader>
                      <CardTitle className="text-xl">{guide.name}</CardTitle>
                      <CardDescription>{guide.title}</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      <div className="flex items-center text-sm text-gray-600">
                        <User className="w-4 h-4 mr-2" />
                        <span>{guide.school}</span>
                      </div>
                      <div className="flex items-center text-sm text-gray-600">
                        <span className="text-yellow-500 mr-1">★</span>
                        <span>{guide.rating} ({guide.tours} tours)</span>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* Step 2: Select Time Slot */}
          {step === 2 && selectedGuide && (
            <div className="max-w-4xl mx-auto">
              <Button variant="outline" onClick={goBack} className="mb-6">
                ← Back to Guides
              </Button>
              
              <Card className="mb-6">
                <CardHeader>
                  <div className="flex items-center gap-4">
                    <img 
                      src={selectedGuide.image} 
                      alt={selectedGuide.name}
                      className="w-20 h-20 rounded-full object-cover"
                    />
                    <div>
                      <CardTitle className="text-2xl">{selectedGuide.name}</CardTitle>
                      <CardDescription>{selectedGuide.title}</CardDescription>
                      <CardDescription className="text-sm mt-1">{selectedGuide.school}</CardDescription>
                    </div>
                  </div>
                </CardHeader>
                {selectedGuide.bio && (
                  <CardContent>
                    <p className="text-sm text-gray-600 mb-4">{selectedGuide.bio}</p>
                    {selectedGuide.specialTours && selectedGuide.specialTours.length > 0 && (
                      <div>
                        <h4 className="font-semibold mb-2">Available Tour Routes:</h4>
                        <div className="flex flex-wrap gap-2">
                          {selectedGuide.specialTours.slice(0, 6).map((tour, idx) => (
                            <span key={idx} className="text-xs bg-gray-100 px-3 py-1 rounded-full">
                              {tour}
                            </span>
                          ))}
                          {selectedGuide.specialTours.length > 6 && (
                            <span className="text-xs text-gray-500 px-3 py-1">
                              +{selectedGuide.specialTours.length - 6} more
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                    {selectedGuide.specialties && selectedGuide.specialties.length > 0 && (
                      <div className="mt-4">
                        <h4 className="font-semibold mb-2">Specialties:</h4>
                        <div className="flex flex-wrap gap-2">
                          {selectedGuide.specialties.map((specialty, idx) => (
                            <span key={idx} className="text-xs bg-blue-50 text-blue-700 px-3 py-1 rounded-full">
                              {specialty}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </CardContent>
                )}
              </Card>

              <h2 className="text-3xl font-serif mb-6">Contact Our Tour Guide Instructor</h2>
              <div className="text-center py-12 bg-white rounded-lg shadow-md">
                <Mail className="w-16 h-16 mx-auto text-blue-600 mb-4" />
                <p className="text-gray-700 text-lg mb-2">Feel free to reach out to Sara on her email to learn how to become a tour guide.</p>
                <a 
                  href="mailto:saralyons1993@gmail.com" 
                  className="inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium transition-colors mt-4"
                >
                  <Mail className="w-5 h-5" />
                  saralyons1993@gmail.com
                </a>
              </div>
            </div>
          )}

          {/* Step 3: Student Details Form */}
          {step === 3 && selectedGuide && selectedTimeSlot && (
            <div className="max-w-3xl mx-auto">
              <Button variant="outline" onClick={goBack} className="mb-6">
                ← Back to Time Slots
              </Button>

              <Card className="mb-6">
                <CardHeader>
                  <CardTitle className="text-2xl">Booking Summary</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div className="flex items-center">
                    <User className="w-5 h-5 mr-3 text-gray-600" />
                    <span><strong>Guide:</strong> {selectedGuide.name}</span>
                  </div>
                  <div className="flex items-center">
                    <CalendarIcon className="w-5 h-5 mr-3 text-gray-600" />
                    <span><strong>Date:</strong> {selectedTimeSlot.date}</span>
                  </div>
                  <div className="flex items-center">
                    <Clock className="w-5 h-5 mr-3 text-gray-600" />
                    <span><strong>Time:</strong> {selectedTimeSlot.time}</span>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-2xl">Your Information</CardTitle>
                  <CardDescription>Please provide your details to complete the booking</CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="firstName">First Name *</Label>
                        <Input
                          id="firstName"
                          name="firstName"
                          value={formData.firstName}
                          onChange={handleChange}
                          required
                          placeholder="John"
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label htmlFor="lastName">Last Name *</Label>
                        <Input
                          id="lastName"
                          name="lastName"
                          value={formData.lastName}
                          onChange={handleChange}
                          required
                          placeholder="Doe"
                          className="mt-1"
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="email">Email Address *</Label>
                      <div className="relative mt-1">
                        <Mail className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          placeholder="john.doe@example.com"
                          className="pl-10"
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="phone">Phone Number *</Label>
                      <div className="relative mt-1">
                        <Phone className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={handleChange}
                          required
                          placeholder="(555) 123-4567"
                          className="pl-10"
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="school">What School *</Label>
                      <div className="relative mt-1">
                        <GraduationCap className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                        <Input
                          id="school"
                          name="school"
                          value={formData.school}
                          onChange={handleChange}
                          required
                          placeholder="e.g., NYU, Columbia University, Hunter College"
                          className="pl-10"
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="numberOfPeople">Number of People *</Label>
                      <Select 
                        value={formData.numberOfPeople} 
                        onValueChange={(value) => setFormData({ ...formData, numberOfPeople: value })}
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue placeholder="Select number of people" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="1">1 Person</SelectItem>
                          <SelectItem value="2">2 People</SelectItem>
                          <SelectItem value="3">3 People</SelectItem>
                          <SelectItem value="4">4 People</SelectItem>
                          <SelectItem value="5">5 People</SelectItem>
                          <SelectItem value="6+">6+ People</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label htmlFor="specialRequirements">Special Requirements or Questions</Label>
                      <Textarea
                        id="specialRequirements"
                        name="specialRequirements"
                        value={formData.specialRequirements}
                        onChange={handleChange}
                        rows={4}
                        placeholder="Any special requests, dietary restrictions, accessibility needs, or questions..."
                        className="mt-1"
                      />
                    </div>

                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-sm text-gray-600">
                        By submitting this form, you agree to our terms and conditions. 
                        You will receive a confirmation email with booking details.
                      </p>
                    </div>

                    <Button 
                      type="submit" 
                      className="w-full bg-black text-white hover:bg-gray-800 py-6 text-lg"
                    >
                      Complete Booking
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          )}
        </div>

        {/* Become a Guide Application Form */}
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 mt-16 mb-16">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-serif mb-4">Want to Be a Local Tour Guide?</h2>
            <p className="text-gray-600">
              Share your campus and local experience with visitors and earn money while doing it!
            </p>
            <div className="flex flex-wrap justify-center gap-4 mt-4 mb-8">
              <span className="bg-gray-100 px-4 py-2 rounded-full text-sm">💰 Earn Money</span>
              <span className="bg-gray-100 px-4 py-2 rounded-full text-sm">🎓 Build Skills</span>
              <span className="bg-gray-100 px-4 py-2 rounded-full text-sm">🤝 Help Others</span>
            </div>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="text-2xl">Local Tour Guide Application</CardTitle>
              <CardDescription>Fill out the form below to apply as a local tour guide (Step {guideFormStep} of 4)</CardDescription>
              {/* Progress Bar */}
              <div className="mt-4 flex gap-2">
                {[1, 2, 3, 4].map(step => (
                  <div 
                    key={step}
                    className={`h-2 flex-1 rounded-full ${step <= guideFormStep ? 'bg-black' : 'bg-gray-200'}`}
                  />
                ))}
              </div>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleGuideSubmit} className="space-y-6">
                
                {/* Step 1: Basic Info */}
                {guideFormStep === 1 && (
                  <>
                    <h3 className="font-semibold text-lg border-b pb-2">Basic Information</h3>
                    
                    {/* Name */}
                    <div>
                      <Label htmlFor="guideName">Full Name *</Label>
                      <div className="relative mt-1">
                        <User className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                        <Input
                          id="guideName"
                          name="name"
                          value={guideFormData.name}
                          onChange={handleGuideFormChange}
                          required
                          placeholder="Your full name"
                          className="pl-10"
                        />
                      </div>
                    </div>

                    {/* Email */}
                    <div>
                      <Label htmlFor="guideEmail">Email Address *</Label>
                      <div className="relative mt-1">
                        <Mail className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                        <Input
                          id="guideEmail"
                          name="email"
                          type="email"
                          value={guideFormData.email}
                          onChange={handleGuideFormChange}
                          required
                          placeholder="your.email@school.edu"
                          className="pl-10"
                        />
                      </div>
                    </div>

                    {/* Phone */}
                    <div>
                      <Label htmlFor="guidePhone">Phone Number *</Label>
                      <div className="relative mt-1">
                        <Phone className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                        <Input
                          id="guidePhone"
                          name="phone"
                          type="tel"
                          value={guideFormData.phone}
                          onChange={handleGuideFormChange}
                          required
                          placeholder="(555) 123-4567"
                          className="pl-10"
                        />
                      </div>
                    </div>

                    {/* School */}
                    <div>
                      <Label htmlFor="guideSchool">School/University *</Label>
                      <div className="relative mt-1">
                        <GraduationCap className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                        <Input
                          id="guideSchool"
                          name="school"
                          value={guideFormData.school}
                          onChange={handleGuideFormChange}
                          required
                          placeholder="e.g., NYU, Columbia University"
                          className="pl-10"
                        />
                      </div>
                    </div>

                    {/* Guide Type Selection */}
                    <div>
                      <Label>What type of guide do you want to be? *</Label>
                      <p className="text-sm text-gray-500 mb-3">Select the role that best fits you</p>
                      <div className="space-y-3">
                        <label className="flex items-center p-4 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                          <input
                            type="radio"
                            name="guideType"
                            value="Local Tour Guide"
                            checked={guideFormData.guide_type === 'Local Tour Guide'}
                            onChange={(e) => setGuideFormData({...guideFormData, guide_type: e.target.value})}
                            className="w-5 h-5 text-black focus:ring-black"
                            required
                          />
                          <div className="ml-4">
                            <span className="font-medium">Local Tour Guide</span>
                            <p className="text-sm text-gray-500">Show visitors around your local area, neighborhoods, and hidden gems</p>
                          </div>
                        </label>
                        
                        <label className="flex items-center p-4 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                          <input
                            type="radio"
                            name="guideType"
                            value="Tour Operator"
                            checked={guideFormData.guide_type === 'Tour Operator'}
                            onChange={(e) => setGuideFormData({...guideFormData, guide_type: e.target.value})}
                            className="w-5 h-5 text-black focus:ring-black"
                          />
                          <div className="ml-4">
                            <span className="font-medium">Tour Operator</span>
                            <p className="text-sm text-gray-500">Organize and manage group tours and travel experiences</p>
                          </div>
                        </label>
                        
                        <label className="flex items-center p-4 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                          <input
                            type="radio"
                            name="guideType"
                            value="Campus Tour Guide"
                            checked={guideFormData.guide_type === 'Campus Tour Guide'}
                            onChange={(e) => setGuideFormData({...guideFormData, guide_type: e.target.value})}
                            className="w-5 h-5 text-black focus:ring-black"
                          />
                          <div className="ml-4">
                            <span className="font-medium">Campus Tour Guide</span>
                            <p className="text-sm text-gray-500">Give tours of your college campus to prospective students and families</p>
                          </div>
                        </label>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      {/* Gender */}
                      <div>
                        <Label>Gender</Label>
                        <select 
                          className="w-full border rounded-md p-2 mt-1"
                          value={guideFormData.gender || ''}
                          onChange={(e) => setGuideFormData({...guideFormData, gender: e.target.value})}
                        >
                          <option value="">Select...</option>
                          {['Male', 'Female', 'Non-binary', 'Prefer not to say'].map(opt => (
                            <option key={opt} value={opt}>{opt}</option>
                          ))}
                        </select>
                      </div>

                      {/* Age */}
                      <div>
                        <Label>Age</Label>
                        <Input
                          type="number"
                          min="16"
                          max="50"
                          value={guideFormData.age || ''}
                          onChange={(e) => setGuideFormData({...guideFormData, age: e.target.value})}
                          placeholder="e.g. 21"
                          className="mt-1"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      {/* Academic Year */}
                      <div>
                        <Label>Academic Year *</Label>
                        <select 
                          className="w-full border rounded-md p-2 mt-1"
                          value={guideFormData.academic_year || ''}
                          onChange={(e) => setGuideFormData({...guideFormData, academic_year: e.target.value})}
                          required
                        >
                          <option value="">Select...</option>
                          {['Freshman', 'Sophomore', 'Junior', 'Senior', 'Graduate Student'].map(opt => (
                            <option key={opt} value={opt}>{opt}</option>
                          ))}
                        </select>
                      </div>

                      {/* Admission Type */}
                      <div>
                        <Label>Admission Type</Label>
                        <select 
                          className="w-full border rounded-md p-2 mt-1"
                          value={guideFormData.admission_type || ''}
                          onChange={(e) => setGuideFormData({...guideFormData, admission_type: e.target.value})}
                        >
                          <option value="">Select...</option>
                          {['Admitted as a freshman', 'Transfer student', 'Graduate admission'].map(opt => (
                            <option key={opt} value={opt}>{opt}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      {/* Personality Type */}
                      <div>
                        <Label>Personality Type</Label>
                        <select 
                          className="w-full border rounded-md p-2 mt-1"
                          value={guideFormData.personality_type || ''}
                          onChange={(e) => setGuideFormData({...guideFormData, personality_type: e.target.value})}
                        >
                          <option value="">Select...</option>
                          {['Introverted', 'Somewhat introverted', 'Somewhat extroverted', 'Extroverted'].map(opt => (
                            <option key={opt} value={opt}>{opt}</option>
                          ))}
                        </select>
                      </div>

                      {/* College Rating */}
                      <div>
                        <Label>College Experience Rating</Label>
                        <select 
                          className="w-full border rounded-md p-2 mt-1"
                          value={guideFormData.college_rating || ''}
                          onChange={(e) => setGuideFormData({...guideFormData, college_rating: e.target.value})}
                        >
                          <option value="">Select...</option>
                          {['Excellent', 'Good', 'Average', 'Below average'].map(opt => (
                            <option key={opt} value={opt}>{opt}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <Button 
                      type="button" 
                      onClick={() => setGuideFormStep(2)}
                      className="w-full bg-black text-white hover:bg-gray-800"
                      disabled={!guideFormData.name || !guideFormData.email || !guideFormData.phone || !guideFormData.school || !guideFormData.guide_type || !guideFormData.academic_year}
                    >
                      Next: Academic Info →
                    </Button>
                  </>
                )}

                {/* Step 2: Academic Info */}
                {guideFormStep === 2 && (
                  <>
                    <h3 className="font-semibold text-lg border-b pb-2">Academic Information</h3>
                    
                    {/* Hometown */}
                    <div>
                      <Label>Hometown *</Label>
                      <Input
                        value={guideFormData.hometown || ''}
                        onChange={(e) => setGuideFormData({...guideFormData, hometown: e.target.value})}
                        placeholder="e.g. Arlington, MA"
                        required
                      />
                    </div>

                    {/* Major */}
                    <div>
                      <Label>Major(s) *</Label>
                      <Input
                        value={guideFormData.major || ''}
                        onChange={(e) => setGuideFormData({...guideFormData, major: e.target.value})}
                        placeholder="e.g. Economics"
                        required
                      />
                    </div>

                    {/* Minor */}
                    <div>
                      <Label>Minor(s)</Label>
                      <Input
                        value={guideFormData.minor || ''}
                        onChange={(e) => setGuideFormData({...guideFormData, minor: e.target.value})}
                        placeholder="e.g. Finance"
                      />
                    </div>

                    {/* High School */}
                    <div>
                      <Label>High School</Label>
                      <Input
                        value={guideFormData.high_school || ''}
                        onChange={(e) => setGuideFormData({...guideFormData, high_school: e.target.value})}
                        placeholder="e.g. Arlington High School"
                      />
                    </div>

                    {/* Extracurriculars */}
                    <div>
                      <Label>Extracurricular Activities</Label>
                      <div className="mt-2 grid grid-cols-2 gap-2">
                        {['Greek life', 'Club/Organization', 'Job/Internship', 'ROTC', 
                          'NCAA varsity sport', 'Recreational sport', 'Student government', 
                          'Community service', 'Religious/Cultural group', 'Art/Music/Performance', 
                          'Study abroad', 'Research', 'Other'].map(activity => (
                          <label key={activity} className="flex items-center gap-2 cursor-pointer text-sm">
                            <input
                              type="checkbox"
                              checked={guideExtracurriculars.includes(activity)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setGuideExtracurriculars([...guideExtracurriculars, activity]);
                                } else {
                                  setGuideExtracurriculars(guideExtracurriculars.filter(a => a !== activity));
                                }
                              }}
                              className="accent-black"
                            />
                            {activity}
                          </label>
                        ))}
                      </div>
                    </div>

                    {/* Clubs */}
                    <div>
                      <Label>Clubs, Organizations & Involvement</Label>
                      <Textarea
                        value={guideFormData.clubs || ''}
                        onChange={(e) => setGuideFormData({...guideFormData, clubs: e.target.value})}
                        placeholder="e.g. Freshman League Club, Club Lacrosse, Ocean Conservation Club"
                        rows={2}
                      />
                    </div>

                    {/* Housing Experience */}
                    <div>
                      <Label>Housing Experience</Label>
                      <div className="mt-2 space-y-2">
                        {['Dorm', 'Off-campus house or apartment', 'Fraternity or sorority house', 'Home (Commuter)', 'Other'].map(housing => (
                          <label key={housing} className="flex items-center gap-2 cursor-pointer text-sm">
                            <input
                              type="checkbox"
                              checked={guideHousingExperience.includes(housing)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setGuideHousingExperience([...guideHousingExperience, housing]);
                                } else {
                                  setGuideHousingExperience(guideHousingExperience.filter(h => h !== housing));
                                }
                              }}
                              className="accent-black"
                            />
                            {housing}
                          </label>
                        ))}
                      </div>
                    </div>

                    <div className="flex gap-4">
                      <Button 
                        type="button" 
                        variant="outline"
                        onClick={() => setGuideFormStep(1)}
                        className="flex-1"
                      >
                        ← Back
                      </Button>
                      <Button 
                        type="button" 
                        onClick={() => setGuideFormStep(3)}
                        className="flex-1 bg-black text-white hover:bg-gray-800"
                        disabled={!guideFormData.hometown || !guideFormData.major}
                      >
                        Next: Personal Questions →
                      </Button>
                    </div>
                  </>
                )}

                {/* Step 3: Personal Questions */}
                {guideFormStep === 3 && (
                  <>
                    <h3 className="font-semibold text-lg border-b pb-2">Personal Questions</h3>
                    
                    {/* Bio */}
                    <div>
                      <Label>Bio / About You *</Label>
                      <Textarea
                        name="bio"
                        value={guideFormData.bio}
                        onChange={handleGuideFormChange}
                        required
                        rows={3}
                        placeholder="Hello! My name is ... and I am a ... at ... studying ..."
                      />
                    </div>

                    {/* College Experience */}
                    <div>
                      <Label>Describe your college experience</Label>
                      <Textarea
                        value={guideFormData.college_experience || ''}
                        onChange={(e) => setGuideFormData({...guideFormData, college_experience: e.target.value})}
                        rows={3}
                        placeholder="My college experience so far has been..."
                      />
                    </div>

                    {/* Tip for Students */}
                    <div>
                      <Label>Tip for future students</Label>
                      <Textarea
                        value={guideFormData.tip_for_students || ''}
                        onChange={(e) => setGuideFormData({...guideFormData, tip_for_students: e.target.value})}
                        rows={3}
                        placeholder="One great thing about this school is..."
                      />
                    </div>

                    {/* Favorite Class */}
                    <div>
                      <Label>Favorite class</Label>
                      <Textarea
                        value={guideFormData.favorite_class || ''}
                        onChange={(e) => setGuideFormData({...guideFormData, favorite_class: e.target.value})}
                        rows={2}
                        placeholder="My favorite class was... because..."
                      />
                    </div>

                    {/* Career Goals */}
                    <div>
                      <Label>Career goals</Label>
                      <Textarea
                        value={guideFormData.career_goals || ''}
                        onChange={(e) => setGuideFormData({...guideFormData, career_goals: e.target.value})}
                        rows={2}
                        placeholder="Currently, I am looking into..."
                      />
                    </div>

                    <div className="flex gap-4">
                      <Button 
                        type="button" 
                        variant="outline"
                        onClick={() => setGuideFormStep(2)}
                        className="flex-1"
                      >
                        ← Back
                      </Button>
                      <Button 
                        type="button" 
                        onClick={() => setGuideFormStep(4)}
                        className="flex-1 bg-black text-white hover:bg-gray-800"
                        disabled={!guideFormData.bio}
                      >
                        Next: Tour Details →
                      </Button>
                    </div>
                  </>
                )}

                {/* Step 4: Tour Details & Photos */}
                {guideFormStep === 4 && (
                  <>
                    <h3 className="font-semibold text-lg border-b pb-2">Tour Details</h3>
                    
                    {/* Tour Types */}
                    <div>
                      <Label>Tour Types You Want to Offer *</Label>
                      <p className="text-sm text-gray-500 mb-3">Select all that apply</p>
                      <div className="space-y-3">
                        <label className="flex items-center p-4 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                          <input
                            type="checkbox"
                            name="tourTypeCampus"
                            checked={guideFormData.tourTypeCampus || false}
                            onChange={(e) => setGuideFormData({...guideFormData, tourTypeCampus: e.target.checked})}
                            className="w-5 h-5 rounded border-gray-300 text-black focus:ring-black"
                          />
                          <div className="ml-4">
                            <span className="font-medium">Campus Tour</span>
                            <p className="text-sm text-gray-500">In-person walking tour around your college campus (60-90 mins)</p>
                          </div>
                        </label>
                        
                        <label className="flex items-center p-4 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                          <input
                            type="checkbox"
                            name="tourTypeVideo"
                            checked={guideFormData.tourTypeVideo || false}
                            onChange={(e) => setGuideFormData({...guideFormData, tourTypeVideo: e.target.checked})}
                            className="w-5 h-5 rounded border-gray-300 text-black focus:ring-black"
                          />
                          <div className="ml-4">
                            <span className="font-medium">Video Tour</span>
                            <p className="text-sm text-gray-500">One-hour video call to answer questions about your school (~60 mins)</p>
                          </div>
                        </label>
                      </div>
                    </div>

                    {/* Tour Description */}
                    <div>
                      <Label>Tour Description *</Label>
                      <Textarea
                        name="tourDescription"
                        value={guideFormData.tourDescription}
                        onChange={handleGuideFormChange}
                        required
                        rows={3}
                        placeholder="Describe the tour you would offer - what locations, highlights, and unique perspectives would you share?"
                      />
                    </div>

                    {/* Meeting Point */}
                    <div>
                      <Label>Meeting Point *</Label>
                      <Input
                        value={guideFormData.meeting_point || ''}
                        onChange={(e) => setGuideFormData({...guideFormData, meeting_point: e.target.value})}
                        placeholder="e.g. In front of the main library"
                        required
                      />
                    </div>

                    {/* Photo Upload */}
                    <div>
                      <Label htmlFor="photos">Profile Photos * <span className="text-gray-500 font-normal">(2-5 photos required)</span></Label>
                      <div className="mt-1">
                        {selectedFiles.length > 0 && (
                          <div className="grid grid-cols-5 gap-2 mb-3">
                            {selectedFiles.map((file, index) => (
                              <div key={index} className="relative group">
                                <img
                                  src={URL.createObjectURL(file)}
                                  alt={`Preview ${index + 1}`}
                                  className="w-full h-20 object-cover rounded-lg border"
                                />
                                <button
                                  type="button"
                                  onClick={() => removePhoto(index)}
                                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs hover:bg-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                                >
                                  ×
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                        
                        {selectedFiles.length < 5 && (
                          <div className="flex items-center justify-center w-full">
                            <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100">
                              <div className="flex flex-col items-center justify-center pt-5 pb-6">
                                <Upload className="w-8 h-8 mb-2 text-gray-500" />
                                <p className="text-sm text-gray-500">
                                  Click to upload photos ({selectedFiles.length}/5)
                                </p>
                                <p className="text-xs text-gray-400 mt-1">PNG, JPG up to 5MB each • Min 2, Max 5 photos</p>
                              </div>
                              <input
                                id="photos"
                                name="photos"
                                type="file"
                                accept="image/*"
                                multiple
                                onChange={handleFileChange}
                                className="hidden"
                              />
                            </label>
                          </div>
                        )}
                        
                        <p className={`text-xs mt-2 ${selectedFiles.length < 2 ? 'text-red-500' : 'text-green-600'}`}>
                          {selectedFiles.length < 2 
                            ? `Please add ${2 - selectedFiles.length} more photo${2 - selectedFiles.length > 1 ? 's' : ''}`
                            : `✓ ${selectedFiles.length} photo${selectedFiles.length > 1 ? 's' : ''} selected`
                          }
                        </p>
                      </div>
                    </div>

                    {/* Availability Calendar */}
                    <div>
                      <Label>Availability * <span className="text-gray-500 font-normal">(Select dates and times you're available)</span></Label>
                      <div className="mt-2 border rounded-lg p-4 bg-gray-50">
                        <div className="mb-4">
                          <p className="text-sm font-medium mb-2">Step 1: Select Available Dates</p>
                          <Calendar
                            mode="multiple"
                            selected={selectedDates}
                            onSelect={handleDateSelect}
                            disabled={(date) => date < new Date()}
                            className="mx-auto"
                          />
                        </div>
                        
                        {selectedDates && selectedDates.length > 0 && (
                          <div className="mb-4 border-t pt-4">
                            <p className="text-sm font-medium mb-2">Selected dates ({selectedDates.length}):</p>
                            <div className="flex flex-wrap gap-2">
                              {selectedDates
                                .sort((a, b) => a - b)
                                .map((date, index) => {
                                  const dateKey = date.toDateString();
                                  const times = dateTimeMap[dateKey] || [];
                                  const isEditing = currentEditingDate && currentEditingDate.toDateString() === dateKey;
                                  return (
                                    <button
                                      key={index}
                                      type="button"
                                      onClick={() => setCurrentEditingDate(date)}
                                      className={`inline-flex items-center text-xs px-3 py-2 rounded-lg border transition-colors ${
                                        isEditing 
                                          ? 'bg-black text-white border-black' 
                                          : times.length > 0 
                                            ? 'bg-green-100 text-green-800 border-green-300 hover:bg-green-200'
                                            : 'bg-yellow-100 text-yellow-800 border-yellow-300 hover:bg-yellow-200'
                                      }`}
                                    >
                                      <span>{date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</span>
                                      <span className="ml-2 text-xs opacity-75">
                                        {times.length > 0 ? `(${times.length} times)` : '(no times)'}
                                      </span>
                                      <span
                                        onClick={(e) => { e.stopPropagation(); removeDate(index); }}
                                        className="ml-2 hover:text-red-500 cursor-pointer"
                                      >
                                        <X className="w-3 h-3" />
                                      </span>
                                    </button>
                                  );
                                })}
                            </div>
                            <p className="text-xs text-gray-500 mt-2">Click a date to select times for it</p>
                          </div>
                        )}
                        
                        {/* Time Slots for Selected Date */}
                        {currentEditingDate && (
                          <div className="border-t pt-4">
                            <p className="text-sm font-medium mb-3">
                              Step 2: Select Available Times for{' '}
                              <span className="text-black font-bold">
                                {currentEditingDate.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}
                              </span>
                            </p>
                            <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
                              {timeSlots.map((time) => {
                                const dateKey = currentEditingDate.toDateString();
                                const currentTimes = dateTimeMap[dateKey] || [];
                                const isSelected = currentTimes.includes(time);
                                return (
                                  <button
                                    key={time}
                                    type="button"
                                    onClick={() => toggleTimeSlot(time)}
                                    className={`text-xs py-2 px-1 rounded-md border transition-colors ${
                                      isSelected
                                        ? 'bg-black text-white border-black'
                                        : 'bg-white text-gray-700 border-gray-300 hover:border-gray-400'
                                    }`}
                                  >
                                    {time}
                                  </button>
                                );
                              })}
                            </div>
                            
                            {dateTimeMap[currentEditingDate.toDateString()]?.length > 0 && (
                              <div className="mt-3">
                                <p className="text-xs text-green-600">
                                  ✓ {dateTimeMap[currentEditingDate.toDateString()].length} time(s) selected for this date
                                </p>
                              </div>
                            )}
                          </div>
                        )}
                        
                        {/* Validation Summary */}
                        <div className="mt-4 pt-4 border-t">
                          {(!selectedDates || selectedDates.length === 0) ? (
                            <p className="text-xs text-red-500">Please select at least one available date</p>
                          ) : (
                            <div className="text-xs">
                              {selectedDates.every(date => (dateTimeMap[date.toDateString()] || []).length > 0) ? (
                                <p className="text-green-600">✓ All {selectedDates.length} date(s) have times selected</p>
                              ) : (
                                <p className="text-yellow-600">
                                  ⚠ {selectedDates.filter(date => (dateTimeMap[date.toDateString()] || []).length === 0).length} date(s) still need times selected
                                </p>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-sm text-gray-600">
                        By submitting this application, you agree to our terms and conditions. 
                        Your application will be reviewed by our team, and we'll contact you within 3-5 business days.
                      </p>
                    </div>

                    <div className="flex gap-4">
                      <Button 
                        type="button" 
                        variant="outline"
                        onClick={() => setGuideFormStep(3)}
                        className="flex-1"
                      >
                        ← Back
                      </Button>
                      <Button 
                        type="submit" 
                        className="flex-1 bg-black text-white hover:bg-gray-800"
                        disabled={isGuideSubmitting || selectedFiles.length < 2 || !guideFormData.tourDescription || !guideFormData.meeting_point || selectedDates.length === 0}
                      >
                        {isGuideSubmitting ? 'Submitting...' : 'Submit Application'}
                      </Button>
                    </div>
                  </>
                )}
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default SignUp;